import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown, LayoutGrid, List, Sparkles, Tag, PackageCheck, Search, PackageOpen, ShoppingBag } from "lucide-react";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useProducts, useCategories, getEffectivePrice, type Product } from "@/hooks/useProducts";
import { useCategoryCounts } from "@/hooks/useCategoryCounts";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { supabase } from "@/integrations/supabase/client";
import QuickViewModal from "@/components/QuickViewModal";
import ShopFilterSidebar from "@/components/ShopFilterSidebar";
import ShopProductCard from "@/components/ShopProductCard";

type SortOption = "default" | "best-sellers" | "price-asc" | "price-desc" | "name-asc" | "name-desc";

const PRODUCTS_PER_PAGE = 12;

const sortLabels: Record<SortOption, string> = {
  default: "Default",
  "best-sellers": "Best Sellers",
  "price-asc": "Price: Low to High",
  "price-desc": "Price: High to Low",
  "name-asc": "Name: A – Z",
  "name-desc": "Name: Z – A",
};

const priceRanges = [
  { label: "All Prices", min: 0, max: Infinity },
  { label: "Under £20", min: 0, max: 20 },
  { label: "£20 – £30", min: 20, max: 30 },
  { label: "£30 – £50", min: 30, max: 50 },
  { label: "Over £50", min: 50, max: Infinity },
];

type QuickFilter = "on_sale" | "new_in" | "in_stock";

const quickFilterConfig: { key: QuickFilter; label: string; icon: typeof Tag }[] = [
  { key: "on_sale", label: "On Sale", icon: Tag },
  { key: "new_in", label: "New In", icon: Sparkles },
  { key: "in_stock", label: "In Stock", icon: PackageCheck },
];

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category");
  const searchFromUrl = searchParams.get("search") || "";
  const sortFromUrl = (searchParams.get("sort") as SortOption) || "default";
  const priceFromUrl = parseInt(searchParams.get("price") || "0", 10);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_PAGE);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [quickFilters, setQuickFilters] = useState<Set<QuickFilter>>(new Set());
  const [bestSellerIds, setBestSellerIds] = useState<number[]>([]);
  const [stickySearch, setStickySearch] = useState(false);
  const heroSearchRef = useRef<HTMLDivElement>(null);

  const { recentIds } = useRecentlyViewed();

  // Sticky search on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (heroSearchRef.current) {
        const rect = heroSearchRef.current.getBoundingClientRect();
        setStickySearch(rect.bottom < 0);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch best seller IDs for sort
  useEffect(() => {
    supabase.rpc("top_selling_product_ids" as any, { lim: 100 }).then(({ data }) => {
      if (data) setBestSellerIds(data as number[]);
    });
  }, []);

  const updateParam = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }, [setSearchParams]);

  const selectedPriceIdx = isNaN(priceFromUrl) ? 0 : priceFromUrl;
  const sort = Object.keys(sortLabels).includes(sortFromUrl) ? sortFromUrl : "default";

  const toggleQuickFilter = (f: QuickFilter) => {
    setQuickFilters((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
    setVisibleCount(PRODUCTS_PER_PAGE);
  };

  const setSelectedCategory = (cat: string | null) => {
    updateParam("category", cat);
    setVisibleCount(PRODUCTS_PER_PAGE);
  };

  const setSort = (s: SortOption) => {
    updateParam("sort", s === "default" ? null : s);
    setVisibleCount(PRODUCTS_PER_PAGE);
  };

  const handleSelectPrice = (idx: number) => {
    updateParam("price", idx === 0 ? null : String(idx));
    setVisibleCount(PRODUCTS_PER_PAGE);
  };

  const { data: allProducts = [], isLoading } = useProducts({
    ...(selectedCategory ? { category: selectedCategory } : {}),
    ...(searchFromUrl ? { search: searchFromUrl } : {}),
  });
  const { data: categories = [] } = useCategories();
  const { data: categoryCounts } = useCategoryCounts();

  // Recently viewed products
  const recentProducts = useMemo(() => {
    if (recentIds.length === 0) return [];
    // We need to fetch these separately but we can match from allProducts if they happen to overlap
    return recentIds
      .map((id) => allProducts.find((p) => p.id === id))
      .filter(Boolean) as Product[];
  }, [recentIds, allProducts]);

  const quickFilterCounts = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const range = priceRanges[selectedPriceIdx];
    const base = allProducts.filter((p) => {
      const price = getEffectivePrice(p);
      return price >= range.min && price < range.max;
    });
    return {
      on_sale: base.filter((p) => p.sale_price && p.regular_price && p.sale_price < p.regular_price).length,
      new_in: base.filter((p) => new Date(p.created_at) >= thirtyDaysAgo).length,
      in_stock: base.filter((p) => p.in_stock).length,
    };
  }, [allProducts, selectedPriceIdx]);

  const filtered = useMemo(() => {
    const range = priceRanges[selectedPriceIdx];
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    let result = allProducts.filter((p) => {
      const price = getEffectivePrice(p);
      if (price < range.min || price >= range.max) return false;
      if (quickFilters.has("on_sale") && !(p.sale_price && p.regular_price && p.sale_price < p.regular_price)) return false;
      if (quickFilters.has("in_stock") && !p.in_stock) return false;
      if (quickFilters.has("new_in") && new Date(p.created_at) < thirtyDaysAgo) return false;
      return true;
    });

    switch (sort) {
      case "best-sellers": {
        const idOrder = new Map(bestSellerIds.map((id, i) => [id, i]));
        result.sort((a, b) => (idOrder.get(a.id) ?? 9999) - (idOrder.get(b.id) ?? 9999));
        break;
      }
      case "price-asc":
        result.sort((a, b) => (a.regular_price || 0) - (b.regular_price || 0));
        break;
      case "price-desc":
        result.sort((a, b) => (b.regular_price || 0) - (a.regular_price || 0));
        break;
      case "name-asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }
    return result;
  }, [allProducts, selectedPriceIdx, sort, quickFilters, bestSellerIds]);

  const visibleProducts = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const activeFilterCount = (selectedCategory ? 1 : 0) + (selectedPriceIdx > 0 ? 1 : 0) + quickFilters.size + (searchFromUrl ? 1 : 0);

  const clearFilters = () => {
    setSearchParams({});
    setQuickFilters(new Set());
    setVisibleCount(PRODUCTS_PER_PAGE);
  };

  // Active filters for summary bar
  const activeFilters: { label: string; onRemove: () => void }[] = [];
  if (selectedCategory) activeFilters.push({ label: selectedCategory, onRemove: () => setSelectedCategory(null) });
  if (selectedPriceIdx > 0) activeFilters.push({ label: priceRanges[selectedPriceIdx].label, onRemove: () => handleSelectPrice(0) });
  if (searchFromUrl) activeFilters.push({ label: `"${searchFromUrl}"`, onRemove: () => updateParam("search", null) });
  quickFilters.forEach((f) => {
    const config = quickFilterConfig.find((c) => c.key === f);
    if (config) activeFilters.push({ label: config.label, onRemove: () => toggleQuickFilter(f) });
  });

  const searchInput = (
    <div className="relative w-full">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        value={searchFromUrl}
        onChange={(e) => {
          updateParam("search", e.target.value || null);
          setVisibleCount(PRODUCTS_PER_PAGE);
        }}
        placeholder="Search products..."
        aria-label="Search products"
        className="w-full pl-11 pr-10 py-3 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 text-sm font-body shadow-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
      />
        {searchFromUrl && (
          <button
            onClick={() => { updateParam("search", null); setVisibleCount(PRODUCTS_PER_PAGE); }}
            aria-label="Clear search"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={selectedCategory ? `${selectedCategory} Hair Products` : "Shop All Hair Products"}
        description={selectedCategory ? `Browse our ${selectedCategory} collection. Premium quality hair products with fast UK delivery.` : "Shop premium wigs, extensions, braids and ponytails. Quality hair products with fast UK delivery from Plymhair."}
        url="/shop"
      />
      <Ticker />
      <Navbar />

      {/* Sticky search bar */}
      <div className={`fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-b border-border/50 shadow-sm transition-all duration-300 ${
        stickySearch ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      }`}>
        <div className="container py-2.5 max-w-lg mx-auto">
          {searchInput}
        </div>
      </div>

      {/* Hero banner */}
      <section className="relative bg-gradient-to-br from-secondary via-secondary to-muted/60 py-12 md:py-14 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="container relative">
          <p className="font-body text-[10px] uppercase tracking-[0.3em] text-primary font-semibold mb-2">Collection</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Shop</h1>
          <p className="text-muted-foreground font-body mt-2 max-w-md text-sm">Browse our full collection of premium hair products</p>

          {/* Search bar */}
          <div className="mt-5 max-w-md" ref={heroSearchRef}>
            {searchInput}
          </div>
        </div>
      </section>

      <div className="container py-8 md:py-10 flex-1">
        {/* Active filters summary bar */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <span className="text-xs font-body text-muted-foreground mr-1">Active:</span>
            {activeFilters.map((f) => (
              <span
                key={f.label}
                className="inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 text-xs font-body font-medium"
              >
                {f.label}
                <button onClick={f.onRemove} className="hover:text-primary/70 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              onClick={clearFilters}
              className="text-xs font-body text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1 transition-colors"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Quick filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {quickFilterConfig.map(({ key, label, icon: Icon }) => {
            const active = quickFilters.has(key);
            const count = quickFilterCounts[key];
            return (
              <button
                key={key}
                onClick={() => toggleQuickFilter(key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-body font-medium transition-all duration-200 border shadow-sm ${
                  active
                    ? "bg-primary text-primary-foreground border-primary shadow-primary/20"
                    : "bg-background text-foreground border-border/70 hover:border-primary/40 hover:shadow-md"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full text-[10px] font-bold px-1 transition-all duration-300 ${
                    active
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-secondary text-muted-foreground"
                  } ${count > 0 ? "opacity-100" : "opacity-40"}`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div id="shop-toolbar" className="flex items-center justify-between mb-6">
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="lg:hidden flex items-center gap-2.5 text-sm font-body font-medium bg-background border border-border/70 px-5 py-2.5 rounded-full shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-200"
          >
            <SlidersHorizontal className="w-4 h-4 text-primary" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-in fade-in zoom-in duration-200">
                {activeFilterCount}
              </span>
            )}
          </button>
          <p className="text-sm text-muted-foreground font-body hidden lg:block">
            Showing {filtered.length} products
          </p>

          <div className="flex items-center gap-2.5">
            <div className="hidden sm:flex items-center border border-border/70 rounded-full overflow-hidden shadow-sm">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 transition-colors ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"}`}
                aria-label="Grid view"
                title="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"}`}
                aria-label="List view"
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortOption)}
                aria-label="Sort products"
                className="appearance-none bg-background border border-border/70 rounded-full px-4 py-2.5 pr-9 text-sm font-body shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 hover:shadow-md transition-shadow"
              >
                {Object.entries(sortLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
            </div>
          </div>
        </div>

        <div className="flex gap-10" id="product-grid">
          <ShopFilterSidebar
            className="hidden lg:block w-56 shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto"
            categories={categories}
            selectedCategory={selectedCategory}
            selectedPriceIdx={selectedPriceIdx}
            activeFilterCount={activeFilterCount}
            priceRanges={priceRanges}
            categoryCounts={categoryCounts}
            onSelectCategory={setSelectedCategory}
            onSelectPrice={handleSelectPrice}
            onClearFilters={clearFilters}
          />

          <div id="shop-results-top" className="flex-1 min-h-[600px]">
            <p className="text-sm text-muted-foreground font-body mb-4 lg:hidden">
              {filtered.length} products
            </p>

            {isLoading ? (
              <div className={viewMode === "grid" ? "grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-8" : "space-y-4"}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    {viewMode === "grid" ? (
                      <>
                        <div className="aspect-[3/4] bg-secondary rounded-lg mb-3 overflow-hidden">
                          <div className="w-full h-full bg-gradient-to-br from-secondary to-muted animate-pulse" />
                        </div>
                        <div className="h-2.5 bg-secondary rounded w-3/4 mb-2" />
                        <div className="h-2.5 bg-secondary rounded w-1/4" />
                      </>
                    ) : (
                      <div className="flex gap-4">
                        <div className="w-36 h-36 bg-secondary rounded-lg shrink-0" />
                        <div className="flex-1 space-y-2 py-2">
                          <div className="h-3 bg-secondary rounded w-2/3" />
                          <div className="h-2.5 bg-secondary rounded w-1/2" />
                          <div className="h-2.5 bg-secondary rounded w-1/4" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              /* Animated empty state */
              <div className="text-center py-20 animate-in fade-in zoom-in-95 duration-500">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary/80 flex items-center justify-center">
                  <PackageOpen className="w-9 h-9 text-muted-foreground/50" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">No products found</h3>
                <p className="text-muted-foreground font-body text-sm mb-6 max-w-xs mx-auto">
                  Try adjusting your filters or search terms to find what you're looking for.
                </p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-body text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                <div className={
                  viewMode === "grid"
                    ? "grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-8"
                    : "space-y-4"
                }>
                  {visibleProducts.map((product, i) => (
                    <ShopProductCard
                      key={product.id}
                      product={product}
                      onQuickView={setQuickViewProduct}
                      index={i}
                      listView={viewMode === "list"}
                    />
                  ))}
                </div>

                {/* Load More button */}
                {hasMore && (
                  <div className="flex justify-center mt-10">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + PRODUCTS_PER_PAGE)}
                      className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 rounded-full font-body text-sm font-medium hover:bg-foreground/90 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      Load More
                      <span className="text-xs opacity-70">
                        ({filtered.length - visibleCount} remaining)
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Recently Viewed */}
        {recentProducts.length > 0 && (
          <div className="mt-16 pt-10 border-t border-border/50">
            <h2 className="font-display text-xl font-bold mb-6">Recently Viewed</h2>
            <div className="flex gap-5 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory scrollbar-thin">
              {recentProducts.map((product) => (
                <div key={product.id} className="w-44 shrink-0 snap-start">
                  <ShopProductCard
                    product={product}
                    onQuickView={setQuickViewProduct}
                    index={0}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Filter Sheet */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${
          mobileFiltersOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
        <div
          className={`absolute bottom-0 left-0 right-0 max-h-[85vh] bg-background rounded-t-3xl overflow-hidden shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            mobileFiltersOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
          </div>
          <div className="flex items-center justify-between px-6 pb-4 border-b border-border/50">
            <div>
              <h2 className="font-display font-bold text-lg">Filters</h2>
              {activeFilterCount > 0 && (
                <p className="text-xs text-muted-foreground font-body mt-0.5">{activeFilterCount} active</p>
              )}
            </div>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="w-9 h-9 rounded-full bg-secondary/80 flex items-center justify-center hover:bg-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-5rem)]">
            <ShopFilterSidebar
              categories={categories}
              selectedCategory={selectedCategory}
              selectedPriceIdx={selectedPriceIdx}
              activeFilterCount={activeFilterCount}
              priceRanges={priceRanges}
              categoryCounts={categoryCounts}
              onSelectCategory={(cat) => { setSelectedCategory(cat); setMobileFiltersOpen(false); }}
              onSelectPrice={(idx) => { handleSelectPrice(idx); setMobileFiltersOpen(false); }}
              onClearFilters={() => { clearFilters(); setMobileFiltersOpen(false); }}
            />
          </div>
        </div>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        open={!!quickViewProduct}
        onOpenChange={(open) => !open && setQuickViewProduct(null)}
      />

      <Footer />
    </div>
  );
};

export default Shop;
